/**
 * The booking dialog, lifted out of the schedule page.
 *
 * She books from wherever she happens to be — the front desk mid-morning, a
 * client's record while they're standing there — not only from the calendar.
 * Keeping the form here means every one of those entry points opens the same
 * dialog with the same validation, instead of three drifting copies.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CalendarRange, CalendarX2, Check, ChevronDown, ChevronUp, CreditCard, Plus, Search, User, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { keepVisitTime } from "@/lib/visit-time";
import { reseatLines, reseatPlan, reseatSummary, type ReseatMove } from "@/lib/reseat";
import { cn } from "@/lib/utils";
import { readTypedTime } from "@/lib/time-input";
import { displayDayKey, displayTimeZone, formatMoney, formatTime, fullName } from "@/lib/format";
import {
  Button, Input, Label, Modal, ModalFooter, Select, Skeleton, useConfirm, useToast,
} from "@/components/ui";
import type {
  Appointment, AppointmentSegment, Contact, Provider, Room, Service, VisitAddon,
} from "@/lib/types";

/* ─── Booking types ────────────────────────────────────────────────────────── */

/**
 * An appointment is a VISIT: `segments` is the ordered list of services it's
 * made of (see `Appointment` in lib/types). The booking engine speaks the same
 * shape when it hands back open slots, one leg at a time.
 */
export interface SlotSegment {
  service_id: string;
  service_name: string;
  /** Null while nobody is assigned - the API has always allowed it. */
  provider_id: string | null;
  room_id: string | null;
  start: string;
  end: string;
  blocked_until: string;
  duration_minutes: number;
  price_cents: number;
}

export interface VisitSlot {
  start: string;
  end: string;
  provider_id: string | null;
  room_id: string | null;
  segments: SlotSegment[];
  /** Per leg: every seat free for it at this time, the engine's pick first. */
  seat_options?: string[][];
  /** Seat id → a heads-up about that chair ("in use next door"). */
  seat_notes?: Record<string, string>;
  /** Moved to a time the desk typed rather than one the engine offered. */
  manual?: boolean;
}

export interface SlotsResponse {
  timezone: string;
  duration_minutes: number;
  services: { id: string; name: string; duration_minutes: number; price_cents: number }[];
  slots: VisitSlot[];
  /** Why the list is empty, when it is. Shown verbatim to the desk. */
  reason?: string;
}

/** Dot colour per palette name — shared by the dialog and the day grid. */
export const DOT_CLASSES: Record<string, string> = {
  violet: "bg-violet-400",
  purple: "bg-purple-400",
  fuchsia: "bg-fuchsia-400",
  pink: "bg-pink-400",
  rose: "bg-rose-400",
  red: "bg-red-400",
  orange: "bg-orange-400",
  amber: "bg-amber-400",
  yellow: "bg-yellow-400",
  green: "bg-green-400",
  emerald: "bg-emerald-400",
  teal: "bg-teal-400",
  cyan: "bg-cyan-400",
  sky: "bg-sky-400",
  blue: "bg-blue-400",
  indigo: "bg-indigo-400",
  slate: "bg-slate-400",
  zinc: "bg-zinc-400",
};

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Every visit as a list of segments. A plain single-service booking still
 * yields exactly one, so callers never special-case it.
 */
export function visitSegments(a: Appointment): AppointmentSegment[] {
  if (a.segments && a.segments.length > 0) {
    return [...a.segments].sort((x, y) => x.position - y.position);
  }
  return [{
    id: `${a.id}:0`,
    service_id: a.service_id,
    provider_id: a.provider_id,
    room_id: a.room_id,
    starts_at: a.starts_at,
    ends_at: a.ends_at,
    blocked_until: a.blocked_until ?? a.ends_at,
    price_cents: a.price_cents,
    position: 0,
    addons: [],
    service_name: a.service_name ?? null,
    service_color: a.service_color ?? null,
    provider_name: a.provider_name ?? null,
    room_name: a.room_name ?? null,
    room_color: a.room_color ?? null,
  }];
}

export type FormMode =
  /** `contact` pre-fills the client — set when booking from their record, so
   *  nobody re-types the name of the person standing at the desk. */
  | { kind: "create"; contact?: Contact | null }
  | { kind: "reschedule"; appointment: Appointment };

/* ─── Service picker ───────────────────────────────────────────────────────── */

/**
 * Type-to-find over the whole menu. Her video 18: the plain dropdown holds
 * 170 treatments, so "add a butterfly push" meant scrolling past every drip
 * in the list. Typing filters on the name AND the category, so "spec" finds
 * the specialized drips and "b12" finds the injection.
 */
function ServicePicker({
  groups,
  onPick,
}: {
  groups: [string, Service[]][];
  onPick: (id: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out: { service: Service; category: string }[] = [];
    for (const [category, list] of groups) {
      for (const service of list) {
        if (!q || service.name.toLowerCase().includes(q) || category.toLowerCase().includes(q)) {
          out.push({ service, category });
        }
      }
    }
    return out.slice(0, 60);
  }, [groups, query]);

  useEffect(() => { setCursor(0); }, [query]);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const choose = (id: string) => {
    onPick(id);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <Input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setCursor((c) => Math.min(c + 1, matches.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
          else if (e.key === "Enter") {
            const hit = matches[cursor];
            if (open && hit) { e.preventDefault(); choose(hit.service.id); }
          } else if (e.key === "Escape" && open) { e.preventDefault(); setOpen(false); }
        }}
        placeholder="Add a service… (type to search)"
        leftIcon={<Search className="h-4 w-4" />}
        role="combobox"
        aria-expanded={open}
        aria-label="Add a service"
      />
      {open && (
        <div
          role="listbox"
          className="absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950 py-1 shadow-xl shadow-black/40"
        >
          {matches.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-zinc-500">Nothing matches "{query}".</p>
          ) : (
            matches.map(({ service, category }, i) => (
              <button
                key={service.id}
                type="button"
                role="option"
                aria-selected={i === cursor}
                onMouseEnter={() => setCursor(i)}
                onClick={() => choose(service.id)}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left",
                  i === cursor ? "bg-zinc-800/70" : "hover:bg-zinc-800/40",
                )}
              >
                <span className={cn("h-2 w-2 shrink-0 rounded-full", DOT_CLASSES[service.color] ?? "bg-zinc-500")} />
                <span className="min-w-0 flex-1 truncate text-sm text-zinc-100">{service.name}</span>
                <span className="shrink-0 text-[11px] text-zinc-500">{category}</span>
                <span className="shrink-0 text-xs tabular-nums text-zinc-400">{formatMoney(service.price_cents)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* ─── New / reschedule dialog ──────────────────────────────────────────────── */

/** A spot the desk clicked on the day grid ("Select time on calendar"). */
export interface CalendarPick {
  /** Bumped on every pick so the same spot twice still re-asks the engine. */
  token: number;
  /** The exact instant under the pointer, snapped to the seat's own grid. */
  start: string;
  /** The calendar's day, in the practice's timezone. */
  date: string;
  room_id: string | null;
  provider_id: string | null;
}

export function AppointmentFormModal({
  mode,
  services,
  providers,
  defaultDate,
  onClose,
  onSaved,
  onPickOnCalendar,
  calendarPick,
  hidden,
}: {
  mode: FormMode;
  services: Service[];
  providers: Provider[];
  defaultDate: string;
  onClose: () => void;
  onSaved: () => void;
  /** Offered only where there IS a calendar to click (the schedule page). */
  onPickOnCalendar?: (ctx: { date: string; label: string; minutes: number }) => void;
  calendarPick?: CalendarPick | null;
  /** Parked while she places the visit on the calendar — state is kept. */
  hidden?: boolean;
}) {
  const toast = useToast();
  const isReschedule = mode.kind === "reschedule";
  const confirm = useConfirm();
  const [cancelling, setCancelling] = useState(false);
  const existing = isReschedule ? mode.appointment : null;

  const [selectedContact, setSelectedContact] = useState<Contact | null>(
    mode.kind === "create" ? (mode.contact ?? null) : null,
  );
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Contact[]>([]);
  const [searching, setSearching] = useState(false);

  /* Ordered service list — one entry per leg of the visit. */
  const [serviceIds, setServiceIds] = useState<string[]>(() => {
    if (!existing) return [];
    const fromSegments = visitSegments(existing)
      .map((s) => s.service_id)
      .filter((id): id is string => !!id);
    if (fromSegments.length > 0) return fromSegments;
    return existing.service_id ? [existing.service_id] : [];
  });
  /* Boosters chosen per leg, by index — "add-ons" on her Boulevard screen. */
  const [addonIds, setAddonIds] = useState<Record<number, string[]>>(() => {
    if (!existing) return {};
    const out: Record<number, string[]> = {};
    visitSegments(existing).forEach((sg, i) => {
      if (sg.addons?.length) out[i] = sg.addons.map((a) => a.service_id);
    });
    return out;
  });
  /**
   * How long a treatment runs for THIS client.
   *
   *   "Because NAD sometimes can go in a shorter amount of time - so we can
   *    add it on sometimes it just depends on the client"
   *
   * The menu's three hours is the usual, not the rule. `minsText` is what is
   * in the box while she types; `customMins` is what the engine and the save
   * use, taken on blur so the times on offer are not redrawn on every
   * keystroke. An empty box means the menu length.
   */
  const [customMins, setCustomMins] = useState<Record<string, number>>({});
  const [minsText, setMinsText] = useState<Record<string, string>>({});
  /* Changing a visit's treatments renames it; an ordinary reschedule doesn't. */
  const [servicesTouched, setServicesTouched] = useState(false);
  const [providerId, setProviderId] = useState(existing?.provider_id ?? "");
  // "auto send but if we check off not to send it won't" — hers verbatim.
  const [notifyClient, setNotifyClient] = useState(existing ? existing.notify_client !== false : true);
  /**
   * "the check box in appointments is default checked can we default
   *  unchecked for sending paperwork"
   *
   * Off by default for anything the DESK books: most of those are people who
   * signed everything at a consultation days ago, and paperwork landing on
   * them again is the complaint. A visit already on the books keeps whatever
   * it was given. Nothing here touches the online booking page, which still
   * sends a first-timer their forms on its own.
   */
  /* "I'm sorry I made a mistake I mean the default for paperwork should send
     and if u don't want to use uncheck" - ticked to begin with, said the way
     round she reads it, and one click turns it off for the person who signed
     everything last week. */
  const [sendForms, setSendForms] = useState(existing ? existing.send_forms !== false : true);
  /**
   * Is THIS visit being billed to insurance?
   *
   * Promised 31 Oct: "The tick on the treatment is really a default, so I am
   * going to add one on the visit itself." null means nobody has said, so the
   * treatments decide; true or false is her answer and overrules the menu.
   */
  const [billsInsurance, setBillsInsurance] = useState<boolean | null>(
    existing?.bills_insurance ?? null,
  );
  const [followupLab, setFollowupLab] = useState(existing?.followup_lab_date ?? "");
  const [followupConsult, setFollowupConsult] = useState(existing?.followup_consult_date ?? "");
  const [date, setDate] = useState(existing ? displayDayKey(new Date(existing.starts_at)) : defaultDate);

  const [slots, setSlots] = useState<VisitSlot[]>([]);
  /** The one time she placed by hand on the calendar — off-grid is fine. */
  const [placedSlot, setPlacedSlot] = useState<VisitSlot | null>(null);
  const [placing, setPlacing] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsLoaded, setSlotsLoaded] = useState(false);
  const [slotsReason, setSlotsReason] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlotRaw] = useState<VisitSlot | null>(null);
  const [ownTimeOpen, setOwnTimeOpen] = useState(false);
  const [ownTime, setOwnTime] = useState("");
  const [saving, setSaving] = useState(false);

  /* Seats — her video: "the booking modal does not indicate which room or
     chair is being booked for each time slot". The desk can pick the chair
     first ("Seat"), and every time shows the chair it takes; after choosing a
     time she can switch to any other chair free then. */
  const [seats, setSeats] = useState<Room[]>([]);
  const [seatFilter, setSeatFilter] = useState("");
  /* "Can we figure out a way for a box in the appointment that we can
     'override' and select what we want? This will help with mostly the
     transition from blvd to our new set up"

     One tick: every seat on the list whether or not the treatment is linked
     to it, anybody who is in rather than only the people ticked for it, and
     the working day rather than the rota. The one thing it does not do is
     put two people in one chair. */
  const [override, setOverride] = useState(false);
  const [seatChoice, setSeatChoice] = useState<Record<number, string>>({});
  useEffect(() => {
    let cancelled = false;
    api.get<{ rooms: Room[] }>("/rooms")
      .then((d) => { if (!cancelled) setSeats(d.rooms); })
      .catch(() => { /* the times still work without names */ });
    return () => { cancelled = true; };
  }, []);
  const seatById = useMemo(() => new Map(seats.map((r) => [r.id, r])), [seats]);

  /** Picking a time: keep each leg's current chair when it's still free
   *  (changing a visit's time shouldn't move the client), else the engine's. */
  const setSelectedSlot = useCallback((slot: VisitSlot | null) => {
    setSelectedSlotRaw(slot);
    const next: Record<number, string> = {};
    if (slot && existing) {
      const current = visitSegments(existing);
      slot.segments.forEach((_, i) => {
        const was = current[i]?.room_id;
        if (was && slot.seat_options?.[i]?.includes(was)) next[i] = was;
      });
    }
    setSeatChoice(next);
  }, [existing]);

  /* Keyed by index so a duplicate or archived service can't shift the list. */
  const chosen = useMemo(
    () => serviceIds.map((id) => ({ id, service: services.find((s) => s.id === id) ?? null })),
    [serviceIds, services]
  );

  /**
   * Her video 19: "they come in for a drip and want an injection too" —
   * Change visit used to let her move the time and nothing else, so a second
   * treatment meant cancelling and rebooking. Treatments (and their add-ons)
   * are editable here too; the engine re-offers times for the new length and
   * the server conflict-checks whatever she picks.
   */
  /** What this treatment runs for, hers over the menu's. */
  const minutesOf = useCallback(
    (serviceId: string, menu: number | undefined) => customMins[serviceId] ?? menu ?? 0,
    [customMins],
  );
  const totalMinutes = chosen.reduce((m, c) => m + minutesOf(c.id, c.service?.duration_minutes), 0);

  /** Lengths she has changed, for the engine: "<id>:<mins>,…". */
  const durationsParam = useMemo(() => {
    const parts = chosen.flatMap((c) =>
      customMins[c.id] && customMins[c.id] !== c.service?.duration_minutes
        ? [`${c.id}:${customMins[c.id]}`] : []);
    return parts.length > 0 ? parts.join(",") : undefined;
  }, [chosen, customMins]);

  /**
   * What she typed in the minutes box becomes the length the visit is built
   * from - a moment after she stops, not on every keystroke.
   *
   * It used to wait until she clicked away, which is fine for typing and
   * wrong for the little up and down arrows: she clicked 180 down towards 75
   * and "Visit length" sat there saying 4h the whole way. A short pause is
   * enough to keep the total honest without asking the engine for times
   * eleven times on the way down.
   */
  const applyMinutes = (serviceId: string, raw: string | undefined, menu: number) => {
    const n = Math.round(Number(raw));
    setCustomMins((prev) => {
      const next = { ...prev };
      if (raw === undefined || raw.trim() === "" || !Number.isFinite(n) || n === menu) delete next[serviceId];
      else next[serviceId] = Math.min(600, Math.max(5, n));
      return next;
    });
  };
  const minsTimer = useRef<number | null>(null);
  const typeMinutes = (serviceId: string, raw: string, menu: number) => {
    setMinsText((prev) => ({ ...prev, [serviceId]: raw }));
    if (minsTimer.current) window.clearTimeout(minsTimer.current);
    minsTimer.current = window.setTimeout(() => applyMinutes(serviceId, raw, menu), 500);
  };
  /** Leaving the box settles it at once, and puts the tidied figure back. */
  const commitMinutes = (serviceId: string, menu: number) => {
    if (minsTimer.current) { window.clearTimeout(minsTimer.current); minsTimer.current = null; }
    const raw = minsText[serviceId];
    setMinsText((prev) => { const next = { ...prev }; delete next[serviceId]; return next; });
    if (raw === undefined) return;
    applyMinutes(serviceId, raw, menu);
  };

  /* Every add-on on the menu, and the ones offered with each chosen leg. */
  const addonById = useMemo(
    () => new Map(services.filter((s) => s.is_addon).map((s) => [s.id, s])),
    [services],
  );
  const addonsFor = useCallback((service: Service | null): Service[] => {
    if (!service) return [];
    return (service.addon_ids ?? []).flatMap((id) => {
      const a = addonById.get(id);
      return a && !a.archived ? [a] : [];
    });
  }, [addonById]);
  const toggleAddon = (index: number, addonId: string) => {
    setAddonIds((prev) => {
      const current = prev[index] ?? [];
      return {
        ...prev,
        [index]: current.includes(addonId) ? current.filter((x) => x !== addonId) : [...current, addonId],
      };
    });
  };
  /** Treatments plus whatever is going into them — what she'll charge. */
  const totalCents = useMemo(() => {
    let cents = 0;
    chosen.forEach((c, i) => {
      cents += c.service?.price_cents ?? 0;
      for (const id of addonIds[i] ?? []) cents += addonById.get(id)?.price_cents ?? 0;
    });
    return cents;
  }, [chosen, addonIds, addonById]);

  /**
   * Any provider who can perform at least one chosen service — the engine
   * honours a preferred provider wherever they're eligible and falls back to
   * whoever else is free for the rest of the visit.
   */
  const providersForServices = useMemo(() => {
    const ids = new Set<string>();
    for (const c of chosen) {
      for (const pid of c.service?.provider_ids ?? []) ids.add(pid);
    }
    return providers.filter((p) => ids.has(p.id));
  }, [chosen, providers]);

  /* Group services by category for the <optgroup> select. */
  /**
   * What may be BOOKED, which is not the same as what she sells.
   *
   * "threads on section is not a service - it is extra added to drips they
   *  come up as service on the screen and allow 5 min can we switch that and
   *  I have selected the drips that can get add ons so they will show up"
   *
   * Her six add-ons (Add B-Complex, Add Biotin and the rest) are five-minute
   * extras that ride on a drip, and she has already said which drips take
   * them. They were in this list because they are services with a flag, so
   * the desk could book "Add Biotin" on its own and the visit came out five
   * minutes long. They belong on the drip, which is where the add-on picker
   * below already puts them.
   */
  const serviceGroups = useMemo(() => {
    const groups = new Map<string, Service[]>();
    for (const s of services) {
      if (s.archived || s.is_addon) continue;
      const key = s.category_name ?? "Other";
      const list = groups.get(key) ?? [];
      list.push(s);
      groups.set(key, list);
    }
    return Array.from(groups.entries());
  }, [services]);

  /* Debounced client search (create mode only). */
  useEffect(() => {
    if (isReschedule || selectedContact) return;
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    const t = window.setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get<{ contacts: Contact[] }>("/contacts", { q, limit: 8 });
        setResults(res.contacts);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [query, isReschedule, selectedContact]);

  /* Open slots — refetch whenever the service list / date / provider changes. */
  const serviceKey = serviceIds.join(",");
  const fetchSlots = useCallback(async () => {
    if (!serviceKey || !date) {
      setSlots([]);
      setSlotsLoaded(false);
      return;
    }
    setSlotsLoading(true);
    setSelectedSlot(null);
    try {
      const res = await api.get<SlotsResponse>("/booking/slots", {
        service_ids: serviceKey,
        date,
        provider_id: providerId || undefined,
        exclude_appointment_id: existing?.id,
        seat_id: seatFilter || undefined,
        durations: durationsParam,
        override: override ? "1" : undefined,
      });
      setSlots(res.slots);
      setSlotsReason(res.reason ?? null);
      setSlotsLoaded(true);
    } catch (err) {
      setSlots([]);
      setSlotsReason(null);
      setSlotsLoaded(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load open times");
    } finally {
      setSlotsLoading(false);
    }
  }, [serviceKey, date, providerId, existing?.id, seatFilter, durationsParam, override, toast, setSelectedSlot]);

  useEffect(() => {
    void fetchSlots();
  }, [fetchSlots]);

  /* "Select time on calendar": she dropped the visit on the day grid, so ask
     the engine about that one instant instead of the usual list. It answers
     with one time or none — none meaning the seat or the person is busy
     then, which is worth saying out loud rather than booking elsewhere. */
  const pickToken = calendarPick?.token ?? 0;
  useEffect(() => {
    if (!calendarPick || !pickToken || !serviceKey) return;
    let cancelled = false;
    setPlacing(true);
    setDate(calendarPick.date);
    (async () => {
      try {
        const res = await api.get<SlotsResponse>("/booking/slots", {
          service_ids: serviceKey,
          date: calendarPick.date,
          at: calendarPick.start,
          seat_id: calendarPick.room_id || undefined,
          provider_id: calendarPick.provider_id || providerId || undefined,
          // Dropped on a person's column: that person, nobody else. Dropped
          // on a chair: whoever the visit already has, else anyone free.
          pin_provider: calendarPick.provider_id ? "1" : undefined,
          exclude_appointment_id: existing?.id,
          durations: durationsParam,
          override: override ? "1" : undefined,
        });
        if (cancelled) return;
        const slot = res.slots[0];
        if (!slot) {
          toast.error("Nothing free there", "That seat is taken then, or nobody is free to do it. Try another spot.");
          return;
        }
        setPlacedSlot(slot);
        setSelectedSlot(slot);
      } catch (err) {
        if (!cancelled) toast.error(err instanceof ApiError ? err.message : "Couldn't put the visit there");
      } finally {
        if (!cancelled) setPlacing(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickToken]);

  /* The times she's offered, with any hand-placed spot in its right place. */
  const displaySlots = useMemo(() => {
    if (!placedSlot) return slots;
    return [placedSlot, ...slots.filter((s) => s.start !== placedSlot.start)]
      .sort((a, b) => a.start.localeCompare(b.start));
  }, [slots, placedSlot]);

  /* Add-ons are keyed by leg index, so any reshuffle of the list has to
     carry them along — otherwise the magnesium follows the position, not
     the drip it was added to. */
  const remapAddons = (move: (i: number) => number | null) =>
    setAddonIds((prev) => {
      const next: Record<number, string[]> = {};
      for (const [k, v] of Object.entries(prev)) {
        const to = move(Number(k));
        if (to !== null) next[to] = v;
      }
      return next;
    });

  const addService = (id: string) => {
    if (!id) return;
    setServiceIds((prev) => [...prev, id]);
    setServicesTouched(true);
    setSelectedSlot(null);
    setPlacedSlot(null);
  };

  const removeService = (index: number) => {
    setServiceIds((prev) => prev.filter((_, i) => i !== index));
    remapAddons((i) => (i === index ? null : i > index ? i - 1 : i));
    setServicesTouched(true);
    setSelectedSlot(null);
    setPlacedSlot(null);
  };

  const moveService = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= serviceIds.length) return;
    setServiceIds((prev) => {
      const next = [...prev];
      const held = next[index]!;
      next[index] = next[target]!;
      next[target] = held;
      return next;
    });
    remapAddons((i) => (i === index ? target : i === target ? index : i));
    setServicesTouched(true);
    setSelectedSlot(null);
    setPlacedSlot(null);
  };

  const usableSeats = useMemo(() => {
    // Override: the whole list. "It is only allowing me to choose lounge or
    // virtual … I want to do phone call."
    if (override) return seats;
    const ids = new Set<string>();
    for (const c of chosen) {
      if (!c.service?.requires_room) continue;
      for (const rid of c.service.room_ids ?? []) ids.add(rid);
    }
    return seats.filter((r) => ids.has(r.id));
  }, [chosen, seats, override]);
  // A filter seat the visit can no longer use (services changed) is dropped.
  useEffect(() => {
    if (seatFilter && seats.length > 0 && !usableSeats.some((r) => r.id === seatFilter)) setSeatFilter("");
  }, [seatFilter, usableSeats, seats.length]);

  const seatFor = (slot: VisitSlot, i: number): string | null =>
    seatChoice[i] ?? slot.segments[i]?.room_id ?? null;

  /** "Sunroom 1 +2": the chair this time takes, and how many others are free. */
  const slotSeatLabel = (slot: VisitSlot): string => {
    const i = slot.segments.findIndex((sg) => !!sg.room_id);
    if (i < 0) return "";
    // The selected time shows the chair she picked, not the default.
    const rid = (selectedSlot?.start === slot.start ? seatFor(slot, i) : null) ?? slot.segments[i]!.room_id!;
    const more = (slot.seat_options?.[i]?.length ?? 1) - 1;
    return `${seatById.get(rid)?.name ?? "Seat"}${more > 0 ? ` +${more}` : ""}`;
  };

  /**
   * Rebuild the visit at the time it ALREADY has.
   *
   *   "I can't save these changes I can't figure out why"
   *   "It won't let me add the NAD"
   *   "both Peter and Bruce are going over to blvd"
   *
   * She was adding a 3-hour NAD to a visit at lunchtime. Nothing fits in
   * what's left of a shift that ends at five, so the engine offered no times,
   * and Save sat greyed out — on a visit that already HAS a time. Adding a
   * treatment should lengthen the visit where it stands, not make her re-pick
   * an hour. Each treatment keeps the chair and clinician it already had; a
   * newly added one follows the one before it.
   */
  const keepCurrentTime = useCallback((): VisitSlot | null => {
    if (!existing) return null;
    const fallbackProvider = providersForServices[0] ?? providers[0] ?? null;
    const segments = keepVisitTime({
      startsAt: existing.starts_at,
      wanted: chosen.map((c) => ({
        id: c.id,
        name: c.service?.name ?? null,
        duration_minutes: customMins[c.id] ?? c.service?.duration_minutes ?? null,
        price_cents: c.service?.price_cents ?? null,
      })),
      current: visitSegments(existing),
      seatFilter: seatFilter || null,
      fallbackProviderId: existing.provider_id ?? fallbackProvider?.id ?? null,
      fallbackRoomId: existing.room_id ?? null,
    });
    if (!segments || segments.length === 0) return null;
    return {
      manual: true,
      start: segments[0]!.start,
      end: segments[segments.length - 1]!.end,
      provider_id: segments[0]!.provider_id,
      room_id: segments[0]!.room_id,
      segments,
    };
  }, [existing, chosen, customMins, providersForServices, providers, seatFilter]);

  /** What Save will actually write: her pick, else the time it already has. */
  const slotToSave = selectedSlot ?? (isReschedule ? keepCurrentTime() : null);

  /** "at 1:00 PM, running 5h" — spelled out before she presses Save. */
  const keptTimeLabel = useMemo(() => {
    if (selectedSlot || !slotToSave) return null;
    const mins = Math.round(
      (new Date(slotToSave.end).getTime() - new Date(slotToSave.start).getTime()) / 60_000);
    const h = Math.floor(mins / 60), m = mins % 60;
    const span = h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`;
    return `at ${formatTime(slotToSave.start)}, running ${span}`;
  }, [selectedSlot, slotToSave]);

  const canSave = isReschedule
    ? !!slotToSave
    : !!selectedContact && serviceIds.length > 0 && !!selectedSlot;

  /** Call the visit off. Confirmed first, because it messages the client. */
  const cancelVisit = async () => {
    if (!existing) return;
    const ok = await confirm({
      title: "Cancel this visit?",
      description: "It comes off the calendar and off the front desk, and the client is told. Their history keeps it.",
      confirmLabel: "Cancel the visit",
      danger: true,
    });
    if (!ok) return;
    setCancelling(true);
    try {
      await api.patch(`/appointments/${existing.id}`, { status: "cancelled" });
      toast.success("Visit cancelled");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't cancel that visit");
    } finally {
      setCancelling(false);
    }
  };

  /**
   * Move the visit to a time she typed.
   *
   * The engine has already worked out who and which seat for the first
   * offered slot; that assignment is kept and the whole visit slides, so a
   * three-treatment booking keeps its order, its gaps and its seats.
   *
   * The shift is worked out from the practice's own clock, not the browser's:
   * what time the template READS as on screen, against what she typed. That
   * way a desk open in another timezone still books the hour she means.
   */
  const applyOwnTime = (typed: string) => {
    const wantedMins = readTypedTime(typed);
    if (wantedMins === null || !date) return;
    const tz = displayTimeZone() ?? undefined;

    const template = displaySlots[0];
    if (template) {
      // There are times on offer, just not the one she wants: keep the seat
      // and provider the engine worked out and slide the whole visit.
      const shown = new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz,
      }).format(new Date(template.segments[0]!.start));
      const [sh, sm] = shown.split(":").map(Number);
      const shift = (wantedMins - ((sh ?? 0) * 60 + (sm ?? 0))) * 60_000;
      const move = (iso: string) => new Date(new Date(iso).getTime() + shift).toISOString();
      setSelectedSlotRaw({
        ...template, manual: true,
        start: move(template.start), end: move(template.end),
        segments: template.segments.map((sg) => ({ ...sg, start: move(sg.start), end: move(sg.end) })),
      });
      return;
    }

    /**
     * Nothing on offer at all, which is where she actually got stuck:
     * "No open times for Consult Lounge 2 this day" on a Friday, because the
     * only provider for that consult does not work Fridays. The desk is not
     * the booking page - it takes the client in front of it - so build the
     * visit from scratch: her seat if she picked one, the first provider who
     * does the treatment, and the treatments laid end to end from the time
     * she typed. The server still refuses a real clash.
     */
    const provider = providersForServices[0] ?? providers[0];
    if (!provider || chosen.length === 0) return;
    // Midday in the practice's zone on the chosen day, as an anchor we can
    // measure from without guessing the offset ourselves.
    const anchor = new Date(`${date}T12:00:00Z`);
    const shownAtAnchor = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz,
    }).format(anchor);
    const [ah, am] = shownAtAnchor.split(":").map(Number);
    const startMs = anchor.getTime() + (wantedMins - ((ah ?? 0) * 60 + (am ?? 0))) * 60_000;

    let cursor = startMs;
    const segments = chosen.map((c) => {
      const mins = customMins[c.id] ?? c.service?.duration_minutes ?? 30;
      const seg = {
        service_id: c.id,
        service_name: c.service?.name ?? "Visit",
        provider_id: provider.id,
        room_id: seatFilter ?? null,
        start: new Date(cursor).toISOString(),
        end: new Date(cursor + mins * 60_000).toISOString(),
        blocked_until: new Date(cursor + mins * 60_000).toISOString(),
        duration_minutes: mins,
        price_cents: c.service?.price_cents ?? 0,
      };
      cursor += mins * 60_000;
      return seg;
    });
    setSelectedSlotRaw({
      manual: true,
      start: segments[0]!.start,
      end: segments[segments.length - 1]!.end,
      provider_id: provider.id,
      room_id: seatFilter ?? null,
      segments,
    });
  };

  /**
   * Save, and if a chair is in the way offer to move the other client along.
   *
   *   "what if the seat was booked but the room is not booked? … if sunroom
   *    one is booked and we add on a NAD to the appointment and we had another
   *    client booked in that seat can it re seat the booked client to the
   *    empty chair?"
   *
   * The server refuses the first attempt and says who it would move and
   * where. She reads it, and the second attempt carries her yes. Nobody is
   * shuffled behind her back.
   */
  const handleSave = async (reseat = false) => {
    const slot = slotToSave;
    if (!slot) return;
    const segments = slot.segments.map((s, i) => ({
      service_id: s.service_id,
      provider_id: s.provider_id,
      room_id: seatFor(slot, i),
      starts_at: s.start,
      ends_at: s.end,
      price_cents: s.price_cents,
      addon_ids: addonIds[i] ?? [],
    }));
    const title = slot.segments.map((s) => s.service_name).join(" + ");
    setSaving(true);
    try {
      if (isReschedule && existing) {
        const res = await api.patch<{ appointment: Appointment; moved?: ReseatMove[] }>(`/appointments/${existing.id}`, {
          notify_client: notifyClient,
          ...(reseat ? { reseat: true } : {}),
          send_forms: sendForms,
          bills_insurance: billsInsurance,
          followup_lab_date: followupLab || null,
          followup_consult_date: followupConsult || null,
          starts_at: slot.start,
          ends_at: slot.end,
          provider_id: slot.provider_id,
          room_id: segments[0]?.room_id ?? null,
          segments,
          /* Only rename when she changed the treatments herself — a hand
             written title survives an ordinary reschedule. */
          ...(servicesTouched ? { title } : {}),
        });
        toast.success("Visit updated", reseatSummary(res.moved ?? []));
      } else {
        if (!selectedContact) return;
        await api.post<{ appointment: Appointment; moved?: ReseatMove[] }>("/appointments", {
          notify_client: notifyClient,
          ...(reseat ? { reseat: true } : {}),
          send_forms: sendForms,
          bills_insurance: billsInsurance,
          followup_lab_date: followupLab || null,
          followup_consult_date: followupConsult || null,
          contact_id: selectedContact.id,
          title,
          starts_at: slot.start,
          ends_at: slot.end,
          segments,
        });
        toast.success("Appointment booked");
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const moves = reseat ? [] : reseatPlan(err.data);
        if (moves.length > 0) {
          const ok = await confirm({
            title: moves.length === 1 ? "Move the other client to a free chair?" : "Move the other clients to free chairs?",
            description: (
              <span className="space-y-2">
                <span className="block">{err.message}</span>
                {reseatLines(moves, formatTime).map((line) => (
                  <span key={line} className="block text-zinc-200">{line}</span>
                ))}
                <span className="block text-zinc-500">
                  Same time, same clinician, same treatment - only the chair changes, and they are not texted about it.
                </span>
              </span>
            ),
            confirmLabel: moves.length === 1 ? "Move them and save" : "Move them all and save",
          });
          if (ok) { await handleSave(true); return; }
          return;
        }
        toast.error(err.message);
        void fetchSlots();
      } else {
        toast.error(err instanceof ApiError ? err.message : "Failed to save appointment");
      }
    } finally {
      setSaving(false);
    }
  };

  /**
   * The client's standing alert, in front of her BEFORE she books.
   *
   * "The schedule alerts can we have them in red and they need to show up in
   *  the booking box so click book and in the box at the top by the name
   *  needs to be the alert. Also in the general book button."
   *
   * Hers read "NEED AMYLASE/LIPASE* Next iv vitamin consult Dec 2026" — the
   * whole point is that it is read at the moment the next visit is being
   * made, and the front desk card was the only place it appeared. The id can
   * arrive three ways (picked from the search, pre-filled from their record,
   * or the client on the visit being moved), so it is resolved from whichever
   * one the dialog has: a searched or pre-filled contact already carries the
   * alert, and only a reschedule has to go and ask.
   */
  const alertContactId = selectedContact?.id ?? existing?.contact_id ?? null;
  const [schedulingAlert, setSchedulingAlert] = useState<string | null>(
    selectedContact?.scheduling_alert ?? null,
  );
  /** Her other alerts, and any follow-up now due — the chart's whole warning. */
  const [otherAlerts, setOtherAlerts] = useState<string[]>([]);
  /** The same two dates, due or not: what is coming, not only what is late. */
  const [chartFollowups, setChartFollowups] = useState<{ lab: string | null; consult: string | null } | null>(null);
  useEffect(() => {
    if (!alertContactId) { setSchedulingAlert(null); setOtherAlerts([]); return; }
    if (selectedContact && selectedContact.id === alertContactId) {
      setSchedulingAlert(selectedContact.scheduling_alert ?? null);
    }
    let live = true;
    api.get<{ contact: Contact }>(`/contacts/${alertContactId}`)
      .then((d) => {
        if (!live) return;
        setSchedulingAlert(d.contact?.scheduling_alert ?? null);
        setOtherAlerts(d.contact?.alerts ?? []);
        setChartFollowups(d.contact?.followups ?? null);
        /* A new visit starts with whatever the chart already says, so the two
           fields are not blank under a client who has both dates on file:
           "Consultations I am seeing in the chart but they are not filling in
           on the appointment box". A visit that already exists keeps its own
           answer — that is the date set AT that visit — and the server treats
           a date handed straight back unchanged as nothing typed. */
        if (!existing) {
          const f = d.contact?.followups;
          if (f?.lab) setFollowupLab((v) => v || f.lab!);
          if (f?.consult) setFollowupConsult((v) => v || f.consult!);
        }
      })
      .catch(() => { if (live) { setSchedulingAlert(null); setOtherAlerts([]); setChartFollowups(null); } });
    return () => { live = false; };
  }, [alertContactId, selectedContact]);

  const clientLabel = isReschedule
    ? existing?.contact_name || "Walk-in"
    : selectedContact
      ? fullName(selectedContact)
      : null;

  /* What the treatments on this visit say by themselves. Shown so the tick
     reads as an override of something, rather than a question out of
     nowhere. */
  const menuBillsInsurance = useMemo(
    () => chosen.some((c) => c.service?.bills_insurance === true),
    [chosen],
  );
  const insuranceOn = billsInsurance ?? menuBillsInsurance;

  /* The two dates that have NOT come round yet, spelled out. Anything already
     due is in `otherAlerts` and shows in red; this is the rest. */
  const comingFollowups = useMemo(() => {
    if (!chartFollowups) return [];
    const today = displayDayKey(new Date());
    const say = (iso: string | null, what: string) => {
      if (!iso || iso <= today) return null;
      const [y, m, d] = iso.split("-").map(Number);
      const when = new Date(Date.UTC(y, (m || 1) - 1, d || 1))
        .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
      return `${what} ${when}`;
    };
    return [say(chartFollowups.lab, "Next labs"), say(chartFollowups.consult, "Next consultation")]
      .filter((x): x is string => Boolean(x));
  }, [chartFollowups]);

  // Parked while she places the visit on the calendar. Rendering nothing
  // (rather than unmounting) keeps the client, the treatments and the
  // add-ons she already chose — every hook above has run.
  if (hidden) return null;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={isReschedule ? "Change visit" : "New appointment"}
    >
      <div className="space-y-4">
        {/* 1 · Client */}
        <div>
          <Label required={!isReschedule}>Client</Label>
          {clientLabel ? (
            <span className="inline-flex h-8 items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-800/60 pl-3 pr-2 text-sm text-zinc-100">
              <User className="h-3.5 w-3.5 text-zinc-400" />
              {clientLabel}
              {!isReschedule && (
                <button
                  type="button"
                  onClick={() => { setSelectedContact(null); setQuery(""); }}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200"
                  aria-label="Clear client"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ) : (
            <div className="relative">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search clients by name, email, or phone…"
                leftIcon={<Search className="h-4 w-4" />}
                autoFocus
              />
              {query.trim() && (
                <div className="absolute inset-x-0 top-full z-10 mt-1 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl shadow-black/40">
                  {searching ? (
                    <div className="space-y-1.5 p-2">
                      <Skeleton className="h-8 w-full" />
                      <Skeleton className="h-8 w-full" />
                    </div>
                  ) : results.length === 0 ? (
                    <p className="px-3 py-2.5 text-xs text-zinc-500">No matching clients.</p>
                  ) : (
                    results.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => { setSelectedContact(c); setResults([]); }}
                        className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left hover:bg-zinc-800/60"
                      >
                        <span className="truncate text-sm text-zinc-100">{fullName(c)}</span>
                        <span className="shrink-0 text-xs text-zinc-500">{c.phone || c.email || ""}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
          {schedulingAlert && (
            <p
              role="alert"
              className="mt-2 flex items-start gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-2 text-sm font-medium leading-snug text-rose-300"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="min-w-0 whitespace-pre-wrap">{schedulingAlert}</span>
            </p>
          )}
          {/* Everything else standing against them, including a lab or consult
              follow-up that has come due — read before the visit is placed.
              One box however many there are: a stack of red panels reads as
              decoration rather than as a warning. */}
          {otherAlerts.length > 0 && (
            <div
              role="alert"
              className="mt-2 flex items-start gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-2 text-sm font-medium leading-snug text-rose-300"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0 space-y-1">
                {otherAlerts.map((line) => (
                  <p key={line} className="whitespace-pre-wrap">{line}</p>
                ))}
              </div>
            </div>
          )}
          {/* And the follow-ups that are not late yet. Red is for "do something
              about this today", so a date still months out gets its own calmer
              line rather than a fourth warning nobody reads: "the only alert
              that shows in booking box in red is schedule alert … they should
              show in the alert place". */}
          {comingFollowups.length > 0 && (
            <div className="mt-2 flex items-start gap-1.5 rounded-md border border-sky-500/30 bg-sky-500/5 px-2.5 py-2 text-sm leading-snug text-sky-300">
              <CalendarRange className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0 space-y-1">
                {comingFollowups.map((line) => <p key={line}>{line}</p>)}
              </div>
            </div>
          )}
        </div>

        {/* 2 · Services — one visit, back-to-back services in order */}
        <div>
          <div className="flex items-baseline justify-between gap-3">
            <Label required>Services</Label>
            <span className="mb-1.5 text-xs text-zinc-500">
              {totalMinutes > 0 && <>Visit length: {formatDuration(totalMinutes)}</>}
              {totalCents > 0 && <> · {formatMoney(totalCents)}</>}
            </span>
          </div>
          {/* Fifty-odd treatments in a dropdown was a scroll: type two
              letters of the dose instead ("spec", "migraine", "b12"). */}
          <ServicePicker groups={serviceGroups} onPick={addService} />
          {chosen.length === 0 ? (
            <p className="mt-1.5 text-xs text-zinc-500">Add at least one service to see open times.</p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {chosen.map((c, i) => {
                const offered = addonsFor(c.service);
                const picked = addonIds[i] ?? [];
                return (
                <li
                  key={`${c.id}-${i}`}
                  className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-2.5 py-1.5"
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded bg-zinc-800 text-[10px] font-semibold tabular-nums text-zinc-400">
                      {i + 1}
                    </span>
                    <span className={cn("h-2 w-2 shrink-0 rounded-full", DOT_CLASSES[c.service?.color ?? ""] ?? "bg-zinc-500")} />
                    <span className="min-w-0 flex-1 truncate text-sm text-zinc-100">
                      {c.service?.name ?? "Service"}
                    </span>
                    {c.service && (
                      /* "it just depends on the client" — the menu length is
                         in the box, and whatever she puts there is what this
                         visit runs for. */
                      <span className="flex shrink-0 items-center gap-1 text-xs text-zinc-500">
                        <input
                          type="number"
                          min={5}
                          max={600}
                          step={5}
                          value={minsText[c.id] ?? String(minutesOf(c.id, c.service.duration_minutes))}
                          onChange={(e) => typeMinutes(c.id, e.target.value, c.service!.duration_minutes)}
                          onBlur={() => commitMinutes(c.id, c.service!.duration_minutes)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          aria-label={`How long ${c.service.name} runs, in minutes`}
                          title="How long it runs for this client"
                          className={cn(
                            "h-6 w-14 rounded border bg-zinc-900/80 px-1 text-right text-xs tabular-nums focus:outline-none",
                            customMins[c.id] && customMins[c.id] !== c.service.duration_minutes
                              ? "border-indigo-500/60 text-indigo-200"
                              : "border-zinc-700 text-zinc-300 focus:border-zinc-500",
                          )}
                        />
                        min · {formatMoney(c.service.price_cents)}
                      </span>
                    )}
                    <span className="flex shrink-0 items-center">
                      <button
                        type="button"
                        onClick={() => moveService(i, -1)}
                        disabled={i === 0}
                        aria-label="Move earlier"
                        className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200 disabled:pointer-events-none disabled:opacity-30"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveService(i, 1)}
                        disabled={i === chosen.length - 1}
                        aria-label="Move later"
                        className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200 disabled:pointer-events-none disabled:opacity-30"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeService(i)}
                        aria-label={`Remove ${c.service?.name ?? "service"}`}
                        className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </div>
                  {/* What goes INTO the bag. No seat and no extra time — it
                      rides along with the drip, and only changes the price. */}
                  {offered.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-6" data-addons-for={c.id}>
                      <span className="text-[11px] text-zinc-500">Add to this:</span>
                      {offered.map((a) => {
                        const on = picked.includes(a.id);
                        return (
                          <button
                            key={a.id}
                            type="button"
                            aria-pressed={on}
                            onClick={() => toggleAddon(i, a.id)}
                            className={cn(
                              "inline-flex h-6 items-center gap-1 rounded-full border px-2 text-[11px]",
                              on
                                ? "border-emerald-500 bg-emerald-500/15 font-semibold text-emerald-200"
                                : "border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-zinc-100",
                            )}
                          >
                            {on ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                            {a.name.replace(/^Add /, "")}
                            <span className={on ? "text-emerald-300/80" : "text-zinc-500"}>
                              {formatMoney(a.price_cents)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* 3 · Date. No provider picker — her call: "we really don't need to
            know any providers, it's just the seats." The engine still assigns
            a qualified provider behind the scenes (first available), so
            notes stay signed and working hours still shape the open times. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label required>Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); setSelectedSlot(null); setPlacedSlot(null); }}
            />
          </div>
          {usableSeats.length > 1 && (
            <div>
              <Label>Seat</Label>
              <Select
                value={seatFilter}
                onChange={(e) => { setSeatFilter(e.target.value); setSelectedSlot(null); }}
                aria-label="Seat"
              >
                <option value="">Any free seat</option>
                {usableSeats.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </Select>
            </div>
          )}
        </div>

        {/* Her override box. Kept right under the date and the seat, because
            that is where she is standing when the list of times is wrong. */}
        <div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              checked={override}
              onChange={(e) => {
                setOverride(e.target.checked);
                setSelectedSlot(null);
                setPlacedSlot(null);
              }}
              className="h-4 w-4 rounded border-zinc-600 accent-indigo-500"
            />
            Override: my pick, not the system&rsquo;s
          </label>
          <p className="mt-1 pl-6 text-xs text-zinc-500">
            {override
              ? "Every seat, anybody who is in, and 8am to 8pm whatever the rota says. Two people in one chair is still refused."
              : "Turn it on to put someone in a seat or with a clinician the treatment is not set up for, or at a time nobody is rota'd on."}
          </p>
        </div>

        {/* 5 · Open times */}
        {chosen.length > 0 && date && (
          <div>
            <div className="flex items-baseline justify-between gap-3">
              <Label>Open times</Label>
              {/* Boulevard's "Select time on calendar": she said it lets her
                  see the whole day and drop the visit exactly where she
                  wants it, which a list of times can't show. */}
              {onPickOnCalendar && (
                <button
                  type="button"
                  onClick={() => onPickOnCalendar({
                    date,
                    label: chosen.map((c) => c.service?.name).filter(Boolean).join(" + ") || "Visit",
                    minutes: totalMinutes || 30,
                  })}
                  className="mb-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-indigo-300 hover:text-indigo-200"
                >
                  <CalendarRange className="h-3.5 w-3.5" />
                  Select time on calendar
                </button>
              )}
            </div>
            {/*
              "I need to be able to override the seat available when booking if
               possible. Like today I'm trying to book a 12:30 but it only
               allowed me to select what the software has available"

              The list is a suggestion, not a rule: it is trimmed by the
              half-hour grid, the provider's hours and how many people may
              start at once, none of which apply to the desk. So she can type
              the time she wants and the visit moves there, keeping the seat
              and provider the engine had already worked out. The server still
              refuses a real clash - two people in one chair - and says which,
              which is the only thing that genuinely cannot happen.
            */}
            {!slotsLoading && !placing && slotsLoaded && (
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setOwnTimeOpen((v) => !v)}
                  className="text-xs font-medium text-indigo-300 hover:text-indigo-200"
                >
                  {ownTimeOpen ? "Use the times above" : "Set the time myself"}
                </button>
                {ownTimeOpen && (
                  <>
                    <Input
                      type="text"
                      inputMode="text"
                      value={ownTime}
                      onChange={(e) => { setOwnTime(e.target.value); applyOwnTime(e.target.value); }}
                      className="h-8 w-28"
                      placeholder="10:00 or 2pm"
                      aria-label="Start the visit at"
                    />
                    {ownTime && selectedSlot?.manual ? (
                      <span className="text-xs text-amber-300">
                        Booking at {formatTime(selectedSlot.start)} - outside the offered times
                      </span>
                    ) : ownTime.trim() && readTypedTime(ownTime) === null ? (
                      <span className="text-xs text-zinc-500">
                        Type a time like 10:00, 2pm or 2:30pm
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            )}
            {slotsLoading || placing ? (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 w-full" />
                ))}
              </div>
            ) : displaySlots.length === 0 ? (
              slotsLoaded && (
                <div className="flex items-start gap-2 rounded-lg border border-dashed border-zinc-800 bg-zinc-900/30 px-3 py-3 text-xs text-zinc-500">
                  <CalendarX2 className="mt-0.5 h-4 w-4 shrink-0" />
                  {/*
                    A dead end was the problem: "I can't save these changes I
                    can't figure out why", and two clients went to Boulevard
                    over it. Worse, the old line here claimed nobody was
                    scheduled when she WAS - a 3-hour NAD simply cannot finish
                    before the shift ends. So the server says why, and this
                    says what happens if she saves anyway.
                  */}
                  <div className="space-y-1">
                    <p className="text-zinc-400">
                      {slotsReason
                        ?? (seatFilter
                          ? `No open times in ${seatById.get(seatFilter)?.name ?? "that seat"} that day.`
                          : "No open times that day.")}
                    </p>
                    {keptTimeLabel ? (
                      <p className="text-emerald-300/90">
                        Save changes keeps this visit where it is, {keptTimeLabel}.
                      </p>
                    ) : (
                      <p>Try another day, or set the time yourself above.</p>
                    )}
                    {seatFilter && (
                      <p>Or try "Any free seat".</p>
                    )}
                  </div>
                </div>
              )
            ) : (
              <div className="grid max-h-60 grid-cols-3 gap-2 overflow-y-auto pr-0.5 sm:grid-cols-4">
                {displaySlots.map((slot) => {
                  const selected = selectedSlot?.start === slot.start && selectedSlot?.provider_id === slot.provider_id;
                  const seatLabel = slotSeatLabel(slot);
                  return (
                    <button
                      key={`${slot.start}-${slot.provider_id}`}
                      type="button"
                      onClick={() => setSelectedSlot(slot)}
                      data-slot-time={slot.start}
                      className={cn(
                        "rounded-lg border px-1 py-1.5 text-sm font-medium transition-colors",
                        selected
                          ? "border-indigo-500 bg-indigo-600 text-white"
                          : "border-zinc-800 bg-zinc-900/50 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100"
                      )}
                    >
                      <span className="block leading-5">
                        {formatTime(slot.start)}
                        {placedSlot?.start === slot.start && (
                          <span className={cn("ml-1 text-[10px] font-normal", selected ? "text-indigo-100" : "text-indigo-300")}>
                            · placed
                          </span>
                        )}
                      </span>
                      {seatLabel && (
                        <span className={cn("block truncate text-[10px] font-normal leading-4", selected ? "text-indigo-100" : "text-zinc-500")}>
                          {seatLabel}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {selectedSlot && (
              <div className="mt-3 space-y-2.5 rounded-lg border border-zinc-800 bg-zinc-900/30 p-2.5">
                {selectedSlot.segments.map((sg, i) => {
                  const options = selectedSlot.seat_options?.[i] ?? (sg.room_id ? [sg.room_id] : []);
                  const current = seatFor(selectedSlot, i);
                  return (
                    <div key={`${sg.service_id}-${i}`}>
                      <p className="flex items-baseline gap-2 text-[11px] text-zinc-500">
                        <span className="tabular-nums text-zinc-400">{formatTime(sg.start)} – {formatTime(sg.end)}</span>
                        <span className="truncate text-zinc-300">{sg.service_name}</span>
                        {options.length > 0 && <span className="shrink-0">· {options.length === 1 ? "1 seat free" : `${options.length} seats free`}</span>}
                      </p>
                      {current && selectedSlot.seat_notes?.[current] && (
                        <p className="mt-1 text-[11px] text-amber-300/90">{selectedSlot.seat_notes[current]}</p>
                      )}
                      {options.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5" role="radiogroup" aria-label={`Seat for ${sg.service_name}`}>
                          {options.map((rid) => {
                            const seat = seatById.get(rid);
                            const on = current === rid;
                            return (
                              <button
                                key={rid}
                                type="button"
                                role="radio"
                                aria-checked={on}
                                onClick={() => setSeatChoice((prev) => ({ ...prev, [i]: rid }))}
                                className={cn(
                                  "inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs",
                                  on
                                    ? "border-indigo-500 bg-indigo-500/15 font-semibold text-indigo-200"
                                    : "border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-zinc-100",
                                )}
                              >
                                <span className={cn("h-2 w-2 rounded-full", DOT_CLASSES[seat?.color ?? ""] ?? "bg-zinc-500")} />
                                {seat?.name ?? "Seat"}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* "The tick on the treatment is really a default, so I am going to add
          one on the visit itself." The same drip is cash for one client and
          billed for another, so the exception lives on the visit. */}
      <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-zinc-300">
        <input
          type="checkbox"
          checked={insuranceOn}
          onChange={(e) => setBillsInsurance(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-zinc-600 accent-indigo-500"
        />
        <span>
          Bill insurance for this visit
          <span className="mt-0.5 block text-[11px] text-zinc-500">
            {billsInsurance === null
              ? (menuBillsInsurance
                  ? "On because these treatments are billed to insurance. Untick it to make this one cash."
                  : "Off because these treatments are not billed to insurance. Tick it to bill this one.")
              : (
                <>
                  Set for this visit{menuBillsInsurance === billsInsurance ? "" : ", overruling the menu"}.{" "}
                  <button
                    type="button"
                    onClick={() => setBillsInsurance(null)}
                    className="underline hover:text-zinc-300"
                  >
                    Go back to what the treatments say
                  </button>
                </>
              )}
          </span>
        </span>
      </label>

      {/* Follow-ups + the notify tick, last so they read as "before you save".
          Named exactly as the client's chart names them, because they are the
          same two dates: "They do show in the appointment box but need them
          here as well." Set one in either place and both move. */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label>Follow up Lab Work</Label>
          <Input type="date" min="1900-01-01" max="2200-12-31"
            value={followupLab} onChange={(e) => setFollowupLab(e.target.value)} />
          {/* The box used to say only what it would do if left empty, which
              read as "the software didn't do it" under a client whose chart
              already held the date. Say where the date on screen came from. */}
          <p className="mt-1 text-[11px] text-zinc-500">
            {!existing && chartFollowups?.lab && followupLab === chartFollowups.lab
              ? "From their chart. Change it here and it changes there."
              : "Also on their chart, under Alerts."}
          </p>
        </div>
        <div>
          <Label>Follow up Consultations</Label>
          <Input type="date" min="1900-01-01" max="2200-12-31"
            value={followupConsult} onChange={(e) => setFollowupConsult(e.target.value)} />
          <p className="mt-1 text-[11px] text-zinc-500">
            {!existing && chartFollowups?.consult && followupConsult === chartFollowups.consult
              ? "From their chart. Change it here and it changes there."
              : "Left empty, a completed consultation fills it in."}
          </p>
        </div>
      </div>
      <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
        <input
          type="checkbox"
          checked={notifyClient}
          onChange={(e) => setNotifyClient(e.target.checked)}
          className="h-4 w-4 rounded border-zinc-600 accent-indigo-500"
        />
        Text the client {isReschedule ? "the updated details" : "their confirmation"}
      </label>
      {/* Said the way round she reads it: tick it to send. A double negative
          on a box that was also ticked by default was two things to work out
          at once, with a client waiting. */}
      <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
        <input
          type="checkbox"
          checked={sendForms}
          onChange={(e) => setSendForms(e.target.checked)}
          className="h-4 w-4 rounded border-zinc-600 accent-indigo-500"
        />
        Send the paperwork for this visit
      </label>
      <p className="mt-1 pl-6 text-xs text-zinc-500">
        {sendForms
          ? "The forms this treatment needs go out with their confirmation."
          : "No forms go out. They still get their confirmation. Untick it for somebody who has already signed."}
      </p>

      <ModalFooter>
        {/* "Can we go to checkout from either front desk or calendar" — a
            visit open on the calendar is the other place she is standing when
            somebody wants to pay. */}
        {existing?.contact_id && (
          <Link
            to={`/checkout?contact_id=${existing.contact_id}&appointment_id=${existing.id}`}
            onClick={onClose}
            className="mr-auto inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-indigo-300 hover:bg-zinc-800"
          >
            <CreditCard className="h-3.5 w-3.5" /> Check out
          </Link>
        )}
        {/*
          "even if the appointment is today I still should be able to cancel it
           I'm not able to cancel this appointment … I'm able to change but not
           cancel"
          There was no way to: the only Cancel here closed the dialog, which is
          worse than missing, because it looks like the thing she wanted. A
          visit that has not happened can be called off from the form it opens
          in, today's included - the desk is never held to the client-facing
          cancellation window.
        */}
        {isReschedule && existing && existing.status !== "cancelled" && existing.status !== "completed" && (
          <Button
            variant="ghost"
            size="sm"
            loading={cancelling}
            onClick={() => void cancelVisit()}
            className="text-rose-300 hover:bg-rose-500/10"
          >
            Cancel this visit
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
        <Button size="sm" loading={saving} disabled={!canSave} onClick={() => void handleSave()}>
          {isReschedule ? "Save changes" : "Book appointment"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
